/**
 * Chat Service
 * Orchestrates session creation, WebSocket connection and message dispatching.
 */
import { MessageDispatcher } from './message-handler.js';
import { MessageRouter } from './message-router.js';
import { SessionManager } from './session-manager.js';
import { MessageHistory, OutgoingPayload, ReconnectOptions } from './types.js';
import { WebSocketManager } from './websocket-manager.js';
import { clearAllSessionData, getChatId } from './session-storage.js';
import { clearSessionActive, isSessionActive, setSessionActive } from './session-flag-storage.js';

export class ChatService {
  private readonly dispatcher = new MessageDispatcher();
  private readonly wsManager = new WebSocketManager();
  private readonly sessions = new SessionManager();
  private readonly router = new MessageRouter({
    dispatcher: this.dispatcher,
    stopReconnection: () => this.wsManager.stopReconnection(),
    dispatchLoginEvent: () => this.dispatchLoginEvent('connected'),
  });
  private readonly externalLoginHandler = () => this.handleExternalLogin();
  private readonly externalLogoutHandler = () => this.handleExternalLogoutEvent();

  private isReconnecting = false;

  constructor() {
    globalThis.addEventListener('MarianaUserLoggedIn', this.externalLoginHandler);
    globalThis.addEventListener('MarianaUserLoggedOut', this.externalLogoutHandler);
  }

  get reconnecting(): boolean {
    return this.isReconnecting;
  }

  async createAndConnectWithBFF(
    source: string,
    options?: { reconnect?: ReconnectOptions }
  ): Promise<boolean> {
    this.wsManager.configureReconnect(options?.reconnect);
    const { wsUrl } = await this.sessions.createSession(source);
    return this.connect(wsUrl, { isReconnection: false });
  }

  /**
   * Reconnects to an existing session via BFF POST /chat/reconnect.
    * The underlying request uses stored identifiers (reconnectSecret, chatId) — never tokens.
   * Returns true if reconnection succeeded and WebSocket is open.
   */
  async reconnect(): Promise<boolean> {
    const session = this.sessions.getSession();
    if (!session) {
      return false;
    }

    if (!navigator.onLine) {
      return false;
    }

    if (this.wsManager.connected) {
      // Already connected, no need to reconnect
      return true;
    }

    if (this.isReconnecting) {
      return false;
    }

    this.isReconnecting = true;

    try {
      const { wsUrl } = await this.sessions.reconnectSession();
      const connected = await this.connect(wsUrl, { isReconnection: true });

      if (connected) {
      } else {
      }

      return connected;
    } catch (error) {
      return false;
    } finally {
      this.isReconnecting = false;
    }
  }

  async connect(websocketUrl: string, options?: { isReconnection?: boolean }): Promise<boolean> {
    try {
      return await this.wsManager.connect(websocketUrl, {
        onOpen: () => {
          if (!options?.isReconnection) {
            this.dispatchLoginEvent('connected');
          }
        },
        onMessage: event => this.router.handleRaw(event),
        onError: () => {},
        onClose: (event) => {
          if (!event.wasClean) {
            // Scenario: WebSocket closed unexpectedly (network lost, server crashed, etc)
          }
          // Notify listeners that connection was lost (UI can update state)
          this.dispatcher.notify('disconnect', {});
        },
      });
    } catch (error) {
      // Scenario: Exception attempting to connect WebSocket (timeout, network, etc)
      this.dispatcher.notify('error', { message: 'Failed to connect' });
      return false;
    }
  }

  async sendMessage(message: string, extra?: { id?: string; values: string[] }): Promise<void> {
    if (!this.wsManager.connected) {
      // Scenario: Attempt to send message without active connection
      throw new Error('Not connected to Agente');
    }

    const payload: OutgoingPayload = extra ? { message, extra } : { message };
    this.wsManager.send(JSON.stringify(payload));
  }

  onMessage(callback: (data: unknown) => void): void {
    this.dispatcher.on('message', callback);
  }

  onTyping(callback: (data: unknown) => void): void {
    this.dispatcher.on('typing', callback);
  }

  onFinished(callback: (data: unknown) => void): void {
    this.dispatcher.on('finished', callback);
  }

  onFinishedAll(callback: (data: unknown) => void): void {
    this.dispatcher.on('finished_all', callback);
  }

  onError(callback: (data: unknown) => void): void {
    this.dispatcher.on('error', callback);
  }

  onDisconnect(callback: (data: unknown) => void): void {
    this.dispatcher.on('disconnect', callback);
  }

  disconnect(): void {
    this.wsManager.disconnect();
    this.dispatcher.clear();
    clearAllSessionData();
  }

  get connected(): boolean {
    return this.wsManager.connected;
  }

  get intentionallyClosed(): boolean {
    return this.wsManager.intentionallyClosed;
  }

  pauseReconnection(): void {
    this.wsManager.pauseReconnection();
  }

  resumeReconnection(): void {
    this.wsManager.resumeReconnection();
  }

  clearHandlers(): void {
    this.dispatcher.clear();
  }

  hydrateFromStorage(): boolean {
    return this.sessions.hydrateFromStorage();
  }

  getReconnectMessages(): MessageHistory[] {
    return this.sessions.getReconnectMessages() ?? [];
  }

  getReconnectChatCreatedAt(): string | undefined {
    return this.sessions.getReconnectChatCreatedAt();
  }

  private handleExternalLogout(): void {
    this.disconnect();
  }

  private handleExternalLogin(): void {
    if (isSessionActive()) return;

    setSessionActive();
    this.dispatchLoginEvent('connected');
  }

  private handleExternalLogoutEvent(): void {
    if (!isSessionActive()) return;

    clearSessionActive();
    this.handleExternalLogout();
  }

  private async dispatchLoginEvent(reason: 'connected'): Promise<void> {
    const chatId = getChatId();
    if (chatId) {
      window.postMessage(
        {
          type: 'MarianaLogin',
          action: '',
          payload: { chatId, reason },
        },
        globalThis.location.origin
      );
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }
}

export const chatService = new ChatService();
export default chatService;
